import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import ContentHeader from 'components/UI/ContentHeader/ContentHeader'
import { superAxiosRequest } from 'axiosApi'
import Loader from 'components/UI/Loader/Loader'
import useLocalStorageFilter from 'helpers/useLocalStorageFilter'
import AsideFilterStore from 'components/UI/AsideFilter/asideFilterStore'
import SearchBooks from 'components/Modules/Ebook/Books/components/SearchBooks/SearchBooks'
import FilterTextValues from 'components/UI/AsideFilter/FilterTextValues'
import AsideFilter from 'components/UI/AsideFilter/AsideFilter'
import useLocalization from 'hooks/useLocalization'
import st from './SummaryReport.module.scss'
import LineChart from '../../Modules/Reports/Charts/LineChart'
import DealsDistributionChart from './DealsDistributionChart'

// Функция для преобразования цвета из hex в rgb
const hexToRgb = (hex) => {
  if (!hex) return null
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result
    ? `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`
    : null
}

// Конфиг фильтра
const filterCnf = [
  {
    type: 'date_day_picker',
    nameState: ['date_from', 'date_until'],
    label: 'Период',
    isRequired: true,
  },
  {
    nameState: 'funnel',
    placeholder: 'Воронка',
    apiLinkCRM: 'integration/funnels',
    type: 'select',
    label: 'Воронка',
    isRequired: true,
  },
  {
    type: 'select-multi-dynamic',
    apiLinkCRM: 'integration/deal_statuses',
    nameState: 'deal_status',
    label: 'Статусы воронки',
    placeholder: 'Статусы воронки',
    apiConfParams: { funnel_id: 'funnel' },
    dependsOn: 'funnel',
    isRequired: false,
    apiGetResult: (data) => {
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        return Object.values(data).map((status) => ({
          id: status.id,
          name: status.name,
          ...status,
        }))
      }
      if (Array.isArray(data)) return data
      if (data?.results) return data.results
      return []
    },
    optionLabelKey: 'name',
    optionValueKey: 'id',
  },
  {
    type: 'type_selector',
    nameState: 'type',
    label: 'Даты',
    values: [
      { id: 1, name: 'По дням', key: 'days' },
      { id: 2, name: 'По неделям', key: 'weeks' },
      { id: 3, name: 'По месяцам', key: 'months' },
    ],
    query_key: true,
  },
]

// Получение массива дат для оси X
const getAllDatesFromData = (data) => {
  const allDates = new Set()
  Object.values(data).forEach((statusData) => {
    Object.keys(statusData).forEach((date) => allDates.add(date))
  })
  return Array.from(allDates).sort()
}

// Преобразование данных для основного LineChart
const transformDataForChart = (
  responseData,
  statusesData,
  selectedStatuses,
) => {
  if (!responseData || Object.keys(responseData).length === 0) {
    return { labels: [], datasets: [] }
  }

  const availableStatusIds = Object.keys(responseData)
  const statusIdsToShow =
    selectedStatuses?.length > 0
      ? availableStatusIds.filter(
          (id) =>
            selectedStatuses.includes(Number(id)) ||
            selectedStatuses.includes(id),
        )
      : availableStatusIds

  const allDates = getAllDatesFromData(responseData)

  const datasets = statusIdsToShow.map((statusId, index) => {
    const dateData = responseData[statusId] || {}
    const data = allDates.map((date) => dateData[date] || 0)
    const statusInfo = statusesData[statusId] || {}
    const statusName = statusInfo.name || `Статус ${statusId}`

    let color = null
    if (statusInfo.color?.body_color) {
      color = hexToRgb(statusInfo.color.body_color)
    }
    if (!color) {
      const fallbackColors = [
        'rgb(255, 99, 132)',
        'rgb(54, 162, 235)',
        'rgb(255, 206, 86)',
        'rgb(75, 192, 192)',
        'rgb(153, 102, 255)',
        'rgb(255, 159, 64)',
        'rgb(201, 203, 207)',
        'rgb(46, 204, 113)',
        'rgb(231, 76, 60)',
        'rgb(52, 73, 94)',
        'rgb(155, 89, 182)',
        'rgb(26, 188, 156)',
        'rgb(241, 196, 15)',
        'rgb(230, 126, 34)',
        'rgb(142, 68, 173)',
        'rgb(39, 174, 96)',
        'rgb(192, 57, 43)',
        'rgb(44, 62, 80)',
        'rgb(211, 84, 0)',
        'rgb(127, 140, 141)',
      ]
      color = fallbackColors[index % fallbackColors.length]
    }

    return {
      label: statusName,
      data: data,
      borderColor: color,
      backgroundColor: color,
      pointRadius: 5,
      pointBorderWidth: 2,
      pointBorderColor: 'rgb(255, 255, 255)',
      tension: 0.4,
      fill: false,
      statusId: statusId,
    }
  })

  return { labels: allDates, datasets: datasets }
}

// 1. График: Соотношение новых и успешных сделок
const transformNewVsSuccessfulData = (responseData, statusesData) => {
  if (!responseData || Object.keys(responseData).length === 0) {
    return { labels: [], datasets: [] }
  }

  const allDates = getAllDatesFromData(responseData)
  const newDealsData = responseData['2'] || {}
  const newDeals = allDates.map((date) => newDealsData[date] || 0)
  const successfulDealsData = responseData['13'] || {}
  const successfulDeals = allDates.map((date) => successfulDealsData[date] || 0)

  const newStatusInfo = statusesData['2'] || {}
  const successfulStatusInfo = statusesData['13'] || {}

  return {
    labels: allDates,
    datasets: [
      {
        label: newStatusInfo.name || 'Новый запрос',
        data: newDeals,
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgb(54, 162, 235)',
        pointRadius: 5,
        pointBorderWidth: 2,
        pointBorderColor: 'rgb(255, 255, 255)',
        tension: 0.4,
        fill: false,
      },
      {
        label: successfulStatusInfo.name || 'Оплатил и записался в группу',
        data: successfulDeals,
        borderColor: 'rgb(46, 204, 113)',
        backgroundColor: 'rgb(46, 204, 113)',
        pointRadius: 5,
        pointBorderWidth: 2,
        pointBorderColor: 'rgb(255, 255, 255)',
        tension: 0.4,
        fill: false,
      },
    ],
  }
}

const transformDataForDoughnutChart = (statData, statusesData) => {
  if (!statData) {
    return null
  }

  const { deals_count, statuses, deal_managers } = statData

  const fallbackColors = [
    'rgb(255, 99, 132)',
    'rgb(54, 162, 235)',
    'rgb(255, 206, 86)',
    'rgb(75, 192, 192)',
    'rgb(153, 102, 255)',
    'rgb(255, 159, 64)',
    'rgb(201, 203, 207)',
    'rgb(46, 204, 113)',
    'rgb(231, 76, 60)',
    'rgb(52, 73, 94)',
    'rgb(155, 89, 182)',
    'rgb(26, 188, 156)',
    'rgb(241, 196, 15)',
    'rgb(230, 126, 34)',
    'rgb(142, 68, 173)',
    'rgb(39, 174, 96)',
    'rgb(192, 57, 43)',
    'rgb(44, 62, 80)',
    'rgb(211, 84, 0)',
    'rgb(127, 140, 141)',
  ]

  // leftArray - статусы сделок (слева)
  const leftArray = (statuses || []).map((status, index) => {
    const statusInfo = statusesData[status.status_id] || {}

    let color = null
    if (statusInfo.color?.body_color) {
      color = hexToRgb(statusInfo.color.body_color)
    }
    if (!color) {
      color = fallbackColors[index % fallbackColors.length]
    }

    return {
      procent: status.percentage,
      value: status.count,
      label: statusInfo.name || `Статус ${status.status_id}`,
      color: color, // Добавляем цвет
      statusId: status.status_id,
    }
  })

  // rightArray - менеджеры (справа)
  const rightArray = (deal_managers || []).map((manager) => {
    return {
      procent: manager.percentage,
      value: manager.count,
      label: manager.full_name,
    }
  })

  return {
    leftArray: leftArray,
    rightArray: rightArray,
    deals_count: deals_count,
  }
}
// Опции для LineChart
const chartOptions = {
  plugins: {
    tooltip: {
      enabled: true,
      position: 'nearest',
      callbacks: {
        label: function (context) {
          const label = context.dataset.label || ''
          const value = context.parsed.y
          if (value === 0) return null
          return `${label}: ${value}`
        },
      },
      mode: 'index',
      intersect: false,
    },
    legend: {
      display: true,
      position: 'top',
      labels: {
        usePointStyle: true,
        font: { size: 12 },
        padding: 20,
      },
    },
  },
  scales: {
    y: { beginAtZero: true, ticks: { stepSize: 1 } },
    x: { grid: { display: false } },
  },
  elements: { point: { borderColor: 'rgb(255, 255, 255)', hoverRadius: 8 } },
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
}

const SummaryReport = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [filter, updateFilter] = useLocalStorageFilter()
  const [statusesData, setStatusesData] = useState({})
  const [chartData, setChartData] = useState({ labels: [], datasets: [] })
  const [newVsSuccessfulData, setNewVsSuccessfulData] = useState({
    labels: [],
    datasets: [],
  })
  const [doughnutChartData, setDoughnutChartData] = useState(null)
  const [rawData, setRawData] = useState(null)
  const [statData, setStatData] = useState(null)

  const {
    isOpenFilter,
    closeFilter,
    setTextValues,
    changeTextValue,
    filterTextValues,
  } = AsideFilterStore

  const { translate } = useLocalization({
    section: 'reports',
    subsection: 'summary_report',
    dict: {
      filter: 'Filter',
      reset_all: 'Reset all',
      apply: 'Apply',
      search: 'Search',
      start_date: 'Start date',
      end_date: 'End date',
    },
  })

  // Получение статусов для выбранной воронки
  useEffect(() => {
    if (filter?.funnel) {
      superAxiosRequest({
        method: 'get',
        link: `integration/deal_statuses`,
        crm: true,
        params: { funnel_id: filter.funnel },
      })
        .then((response) => setStatusesData(response.data || {}))
        .catch(() => setStatusesData({}))
    } else {
      setStatusesData({})
    }
  }, [filter?.funnel])

  // Формирование параметров запроса
  const getRequestParams = () => {
    const params = {}
    if (filter?.date_from) params.date_from = filter.date_from
    if (filter?.date_until) params.date_until = filter.date_until
    if (filter?.funnel) params.funnel = filter.funnel
    if (filter?.deal_status && filter.deal_status.length) {
      params.deal_status = filter.deal_status.join(',')
    }
    if (filter?.type) {
      if (typeof filter.type === 'object' && filter.type?.key) {
        params.type = filter.type.key
      } else if (typeof filter.type === 'string') {
        params.type = filter.type
      } else if (filter.type?.id) {
        const typeMap = { 1: 'days', 2: 'weeks', 3: 'months' }
        params.type = typeMap[filter.type.id] || filter.type.key
      }
    }
    return params
  }

  // Загрузка данных для основного графика
  const fetchReportData = () => {
    const params = getRequestParams()
    if (!params.date_from || !params.date_until || !params.funnel) return

    setIsLoading(true)
    superAxiosRequest({
      method: 'get',
      link: 'reports/swot_report',
      params: params,
      crm: true,
    })
      .then((response) => {
        setRawData(response.data || {})
        setIsLoading(false)
      })
      .catch(() => {
        setIsLoading(false)
        setRawData(null)
        setChartData({ labels: [], datasets: [] })
        setNewVsSuccessfulData({ labels: [], datasets: [] })
      })
  }

  // Загрузка данных для статистики
  const fetchStatData = () => {
    const params = getRequestParams()
    if (!params.date_from || !params.date_until || !params.funnel) return

    superAxiosRequest({
      method: 'get',
      link: 'reports/swot_report_stat',
      params: params,
      crm: true,
    })
      .then((response) => setStatData(response.data || {}))
      .catch(() => setStatData(null))
  }

  // Обновление графиков при изменении данных
  useEffect(() => {
    if (rawData && Object.keys(rawData).length > 0) {
      setChartData(
        transformDataForChart(rawData, statusesData, filter?.deal_status),
      )
      setNewVsSuccessfulData(
        transformNewVsSuccessfulData(rawData, statusesData),
      )
    }
  }, [rawData, statusesData, filter?.deal_status])

  // Обновление данных для DoughnutChart
  useEffect(() => {
    if (statData && Object.keys(statusesData).length > 0) {
      const doughnutData = transformDataForDoughnutChart(statData, statusesData)
      setDoughnutChartData(doughnutData)
    }
  }, [statData, statusesData])

  // Обновление данных при изменении фильтра
  useEffect(() => {
    const params = getRequestParams()
    if (params.date_from && params.date_until && params.funnel) {
      fetchReportData()
      fetchStatData()
    }
  }, [
    filter?.date_from,
    filter?.date_until,
    filter?.funnel,
    filter?.deal_status,
    filter?.type,
  ])

  if (isLoading) {
    return <Loader />
  }

  return (
    <div>
      <ContentHeader title='Сводный отчет аналитики' />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          margin: '10px',
        }}
      >
        <SearchBooks
          onSearch={(text) => {
            updateFilter({ ...filter, search: text })
            changeTextValue('search', {
              key: 'search',
              label: 'Search',
              value: text,
            })
          }}
          onClear={() => {
            const { search, ...obj } = filter
            updateFilter(obj)
            setTextValues(
              filterTextValues.filter((field) => field.key !== 'search'),
            )
          }}
          translate={translate}
          placeholder={`Поиск...`}
          value={filter?.search || ''}
        />
        {!!filterTextValues.length && (
          <FilterTextValues
            onDelete={(key) => {
              const { [key]: _, ...obj } = filter
              updateFilter(obj)
            }}
          />
        )}
        {isOpenFilter && (
          <AsideFilter
            translate={translate}
            getFilterParams={updateFilter}
            config={filterCnf}
            filterValue={filter}
            onDropFilter={() => {
              updateFilter({})
              closeFilter()
            }}
          />
        )}
      </div>

      {/* ПЕРВЫЙ БЛОК */}
      <div className={st.wrapper}>
        <h3 className={st.sectionTitle}>Динамика сделок по статусам</h3>
        <div className={st.chartContainer}>
          {chartData.datasets.length > 0 && chartData.labels.length > 0 ? (
            <LineChart newData={chartData} options={chartOptions} />
          ) : (
            <div className={st.emptyState}>
              <p>Нет данных для отображения</p>
              <p>Пожалуйста, выберите период и воронку в фильтре</p>
            </div>
          )}
        </div>
      </div>

      {/* ВТОРОЙ БЛОК */}
      <div className={st.chartSection}>
        <h3 className={st.sectionTitle}>Распределение сделок</h3>
        <div className={st.doughnutContainer}>
          {doughnutChartData ? (
            <DealsDistributionChart
              leftArray={doughnutChartData.leftArray}
              rightArray={doughnutChartData.rightArray}
              totalCount={statData?.deals_count || 0}
              totalLabel='Всего сделок'
              managersColumns={2}
            />
          ) : (
            <div className={st.emptyState}>
              <p>Нет данных для отображения</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default observer(SummaryReport)
